//
// HCL Technologies Confidential
// (C) Copyright HCL Technologies 2026. All Rights Reserved.
// The source code for this program is not published or otherwise divested
// of its trade secrets, irrespective of what has been deposited with the
// U.S. Copyright Office.
//

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Dialog, DialogContent } from '../ui/dialog';
import { Button } from '../ui/button';

interface DeleteGroupModalProps {
  isOpen: boolean;
  groupName: string;
  onClose: () => void;
  onConfirm: () => Promise<void>;
}

const DeleteGroupModal: React.FC<DeleteGroupModalProps> = ({
  isOpen,
  groupName,
  onClose,
  onConfirm,
}) => {
  const { t } = useTranslation();
  const [deleting, setDeleting] = useState(false);

  const handleConfirm = async () => {
    try {
      setDeleting(true);
      await onConfirm();
      // Only close on success
      onClose();
    } catch (error) {
      console.error('Error deleting group:', error);
      // Don't close modal on error - let user try again or cancel
    } finally {
      setDeleting(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape' && !deleting) {
      onClose();
    }
  };

  const handleOpenChange = (open: boolean) => {
    // Only allow closing if not currently deleting
    if (!open && !deleting) {
      onClose();
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent
        data-testid="delete-group-modal"
        onKeyDown={handleKeyDown}
        className="sm:max-w-[425px]"
      >
        {/* Header */}
        <div className="mb-4">
          <h2
            className="text-lg font-semibold text-gray-900 dark:text-white"
            data-testid="delete-group-modal-title"
          >
            {t('userGroups.deleteConfirmTitle')}
          </h2>
          <p
            className="mt-2 text-sm text-gray-600 dark:text-gray-400"
            data-testid="delete-group-modal-message"
          >
            {t('userGroups.deleteConfirmMessage', { groupName })}
          </p>
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-2">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={deleting}
            data-testid="delete-group-modal-cancel-button"
          >
            {t('userGroups.cancel')}
          </Button>
          <Button
            variant="destructive"
            onClick={handleConfirm}
            disabled={deleting}
            data-testid="delete-group-modal-delete-button"
            autoFocus
          >
            {deleting ? t('userGroups.deleting') : t('userGroups.delete')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DeleteGroupModal;
